import { supabase } from '../supabase.js'

export const USER_CHART_PREFERENCES_TABLE = 'user_chart_preferences'

export const CHART_PREFERENCE_KEYS = Object.freeze({
  settings: 'chart-settings',
  paneRatio: 'volume-pane-ratio',
  demoShell: 'demo-shell',
  visibleTimeRange: 'visible-time-range',
  visibleLogicalRange: 'visible-logical-range',
})

const PREFERENCE_USER_ID_CACHE_TTL_MS = 30_000
let cachedPreferenceUserId = null
let cachedPreferenceUserIdAt = 0
let cachedPreferenceSessionToken = null
let preferenceUserIdPromise = null

function authenticatedClaimsUserId(claims) {
  const audience = Array.isArray(claims?.aud) ? claims.aud : [claims?.aud]
  if (
    typeof claims?.sub !== 'string'
    || !claims.sub
    || claims.role !== 'authenticated'
    || !audience.includes('authenticated')
  ) return null
  return claims.sub
}

export function __resetPreferenceUserIdCacheForTests() {
  cachedPreferenceUserId = null
  cachedPreferenceUserIdAt = 0
  cachedPreferenceSessionToken = null
  preferenceUserIdPromise = null
}

export async function resolvePreferenceUserId(userId) {
  if (userId) return userId
  if (preferenceUserIdPromise) return preferenceUserIdPromise

  preferenceUserIdPromise = (async () => {
    try {
      // getUser() performs a network validation and can fail during mobile
      // session recovery. Prefer the active session plus verified JWT claims
      // so chart preferences can hydrate as soon as the local session exists.
      if (
        typeof supabase.auth.getSession !== 'function'
        || typeof supabase.auth.getClaims !== 'function'
      ) {
        const { data, error } = await supabase.auth.getUser()
        return error ? null : (data?.user?.id || null)
      }

      const { data: sessionData, error: sessionError } = await supabase.auth.getSession()
      const token = sessionData?.session?.access_token
      if (sessionError || !token) {
        cachedPreferenceUserId = null
        cachedPreferenceUserIdAt = 0
        cachedPreferenceSessionToken = null
        return null
      }
      if (
        cachedPreferenceUserId
        && cachedPreferenceSessionToken === token
        && Date.now() - cachedPreferenceUserIdAt < PREFERENCE_USER_ID_CACHE_TTL_MS
      ) return cachedPreferenceUserId

      const { data, error } = await supabase.auth.getClaims(token)
      if (error) return null
      const resolvedUserId = authenticatedClaimsUserId(data?.claims)
      if (!resolvedUserId) return null

      cachedPreferenceUserId = resolvedUserId
      cachedPreferenceUserIdAt = Date.now()
      cachedPreferenceSessionToken = token
      return resolvedUserId
    } catch {
      return null
    } finally {
      preferenceUserIdPromise = null
    }
  })()

  return preferenceUserIdPromise
}

export function normalizePreferencePayload(value) {
  return value && typeof value === 'object' && !Array.isArray(value) ? value : null
}

export async function readServerChartPreference({
  scope,
  preferenceKey,
  userId = null,
  throwOnError = false,
}) {
  const ownerId = await resolvePreferenceUserId(userId)
  if (!ownerId || !scope || !preferenceKey) return null

  try {
    const { data, error } = await supabase
      .from(USER_CHART_PREFERENCES_TABLE)
      .select('preferences')
      .eq('user_id', ownerId)
      .eq('scope', scope)
      .eq('preference_key', preferenceKey)
      .maybeSingle()

    if (error) throw error
    return normalizePreferencePayload(data?.preferences)
  } catch (error) {
    if (throwOnError) throw error
    return null
  }
}

export async function writeServerChartPreference({
  scope,
  preferenceKey,
  preferences,
  userId = null,
}) {
  const ownerId = await resolvePreferenceUserId(userId)
  const payload = normalizePreferencePayload(preferences)
  if (!ownerId || !scope || !preferenceKey || !payload) return false

  try {
    const { error } = await supabase
      .from(USER_CHART_PREFERENCES_TABLE)
      .upsert({
        user_id: ownerId,
        scope,
        preference_key: preferenceKey,
        preferences: payload,
        updated_at: new Date().toISOString(),
      }, { onConflict: 'user_id,scope,preference_key' })

    return !error
  } catch {
    return false
  }
}
